import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Car, Settings, HardDrive, Router } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import BottomPanel from "./BottomPanel.component";
import { DASHBOARD_PATHS } from "../../constants/paths";

export default function AppSidebar() {
  const { pathname } = useLocation(); // Full current path, e.g., /dashboard/home, /dashboard/fleet/details
  const { t } = useTranslation();

  const isActive = (routePathPattern) => { // routePathPattern is from DASHBOARD_PATHS, e.g., "/", "/fleet/*"
    const baseAppPath = "/dashboard";

    // Handle the HOME case ("/") specifically
    if (routePathPattern === DASHBOARD_PATHS.HOME) {
      return pathname === baseAppPath || pathname === `${baseAppPath}/`;
    }

    // Construct the full pattern to match against, e.g., /dashboard/fleet/* or /dashboard/edge-devices
    const fullMatchPattern = `${baseAppPath}${routePathPattern}`.replace(/\/\//g, '/');

    if (routePathPattern.endsWith("/*")) {
      // For wildcard paths, match the base, e.g., /dashboard/fleet for /dashboard/fleet/*
      const baseRouteToMatch = fullMatchPattern.slice(0, -2);
      return pathname.startsWith(baseRouteToMatch);
    }

    // For non-wildcard paths, check if the current pathname starts with this full path
    // This makes "/dashboard/edge-devices" active for both "/dashboard/edge-devices" and "/dashboard/edge-devices/details"
    return pathname.startsWith(fullMatchPattern);
  };

  const navItems = [
    {
      path: DASHBOARD_PATHS.HOME,
      icon: Home,
      label: t("dashboard.drawer.home", "Home"),
    },
    {
      path: DASHBOARD_PATHS.FLEET,
      icon: Car,
      label: t("dashboard.drawer.fleet", "Fleet"),
    },
    {
      path: DASHBOARD_PATHS.EDGE_DEVICES,
      icon: HardDrive,
      label: t("dashboard.drawer.edgeDevices", "Edge Devices"),
    },
    {
      path: DASHBOARD_PATHS.IOT_GATEWAYS,
      icon: Router,
      label: t("dashboard.drawer.iotGateways", "IoT Gateways"),
    },
    {
      path: DASHBOARD_PATHS.SETTINGS,
      icon: Settings,
      label: t("dashboard.drawer.settings"),
    }
  ];

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="h-16 border-b border-blue-500 bg-blue-600">
        <div className="flex items-center justify-center h-full">
          {/* Logo could go here */}
          <div className="w-8 h-8 bg-white rounded-md flex items-center justify-center">
            <span className="text-blue-600 font-bold text-sm">AL</span>
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="bg-blue-600">
        <SidebarGroup className="px-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                const isItemActive = isActive(item.path);

                let navLinkTargetPath = item.path;
                if (navLinkTargetPath.endsWith("/*")) {
                  navLinkTargetPath = navLinkTargetPath.slice(0, -2); // Remove /* for the NavLink
                }
                const fullPathForNavLink = `/dashboard${navLinkTargetPath}`.replace(/\/\//g, '/');

                return (
                  <SidebarMenuItem key={item.path}> {/* Using item.path as key, assuming they are unique */}
                    <SidebarMenuButton
                      asChild
                      isActive={isItemActive}
                      tooltip={item.label}
                      size="lg"
                      className={cn(
                        "h-[70px] justify-center transition-colors rounded-none border-0",
                        isItemActive 
                          ? "bg-gray-200 text-blue-600 hover:bg-gray-100" 
                          : "text-gray-200 hover:bg-blue-700 hover:text-white"
                      )}
                    >
                      <NavLink to={fullPathForNavLink} className="flex flex-col items-center gap-1">
                        <IconComponent className="w-6 h-6" />
                        <span className="text-xs group-data-[collapsible=icon]:hidden">
                          {item.label}
                        </span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="bg-blue-600 border-t border-blue-500">
        <BottomPanel />
      </SidebarFooter>
    </Sidebar>
  );
} 